import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { postService } from '../services/postService';
import { subscriptionService } from '../services/subscriptionService';
import { SubscriptionRecord } from '../types/subscription';
import {
  canLoadMore,
  initialTimelineState,
  timelineIsEmpty,
  timelineReducer,
  timelineRetryTarget,
} from '../util/postTimeline';
import {
  SubscribeFailure,
  findSubscription,
  formatPeriodEnd,
  subscribeFailure,
  subscriptionAccessState,
  wasResumedWithoutCharge,
} from '../util/subscriptionState';
import SubscribeCard from './SubscribeCard';
import TimelinePostCard from './TimelinePostCard';

interface CreatorTimelineProps {
  creatorId: string;
  creatorName: string;
  isOwnProfile: boolean;
}

/**
 * A creator's posts on their profile, with the subscribe panel above them.
 *
 * Which posts are locked is decided by the backend once per request, so the
 * only way a post unlocks is to read page 1 again after the subscription
 * changes — nothing here can open a post on its own.
 */
const CreatorTimeline: React.FC<CreatorTimelineProps> = ({
  creatorId,
  creatorName,
  isOwnProfile,
}) => {
  const [state, dispatch] = useReducer(timelineReducer, initialTimelineState);
  const [plan, setPlan] = useState<{ monthlyPriceCredits: number } | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionRecord | null>(null);
  const [busy, setBusy] = useState(false);
  const [subscribeError, setSubscribeError] = useState<SubscribeFailure | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(true);
  // The observer callback would otherwise close over the state of the render
  // that created it, and it outlives that render.
  const stateRef = useRef(state);
  stateRef.current = state;
  // One in-flight page at a time, and only its answer is used: an older
  // response arriving late must not append to a list it no longer matches.
  const requestIdRef = useRef(0);
  const inFlightRef = useRef(false);
  // The same guard for the refresh request. A ref, not the `refreshing` state,
  // because state only flips on the next render: two clicks in one tick would
  // both read `false` and fire two page-1 requests.
  const refreshingRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadPage = useCallback(
    async (cursor: string | null, append: boolean) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      inFlightRef.current = true;

      const result = await postService.listCreatorPosts(creatorId, cursor);

      // Superseded or unmounted: the newer request owns the state from here.
      if (!mountedRef.current || requestId !== requestIdRef.current) {
        return;
      }
      inFlightRef.current = false;

      if (!result.success || !result.page) {
        dispatch({ type: 'fail', message: result.message || 'Could not load posts.' });
        return;
      }

      dispatch({ type: 'pageLoaded', page: result.page, append });
    },
    [creatorId],
  );

  /** Page 1 again, replacing what is on screen. Supersedes anything in flight. */
  const reloadFromStart = useCallback(() => {
    dispatch({ type: 'loadFirst' });
    void loadPage(null, false);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (inFlightRef.current || !canLoadMore(stateRef.current)) {
      return;
    }
    dispatch({ type: 'loadMore' });
    void loadPage(stateRef.current.cursor, true);
  }, [loadPage]);

  /**
   * A signed media URL has lapsed. There is no single-post read route on the
   * backend, so this re-reads page 1 and swaps the posts it returns in place —
   * it deliberately leaves `requestIdRef`/`inFlightRef` alone, because it can
   * only replace posts already on screen and must not supersede a page load.
   */
  const handleRefreshPost = useCallback(async () => {
    if (refreshingRef.current || inFlightRef.current) {
      return;
    }

    refreshingRef.current = true;
    setRefreshing(true);
    const result = await postService.listCreatorPosts(creatorId, null);
    refreshingRef.current = false;

    if (!mountedRef.current) {
      return;
    }

    if (result.success && result.page) {
      dispatch({ type: 'postsRefreshed', page: result.page });
    } else {
      toast.error(result.message || 'Could not refresh this post.');
    }

    setRefreshing(false);
  }, [creatorId]);

  const handleRetry = useCallback(() => {
    if (inFlightRef.current) {
      return;
    }
    const target = timelineRetryTarget(stateRef.current);
    dispatch({ type: 'retry' });
    void loadPage(target.cursor, target.append);
  }, [loadPage]);

  // Page 1, plus the plan and the viewer's own subscriptions in parallel.
  useEffect(() => {
    dispatch({ type: 'reset' });
    requestIdRef.current += 1;
    inFlightRef.current = false;
    refreshingRef.current = false;
    setPlan(null);
    setSubscription(null);
    setSubscribeError(null);
    setBusy(false);
    setRefreshing(false);

    // `reset` above put the reducer back to idle, so this always takes.
    dispatch({ type: 'loadFirst' });
    void loadPage(null, false);

    // A creator viewing their own profile has no plan to buy and no
    // subscription to themselves, so neither call is made.
    if (isOwnProfile) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const [planResult, mineResult] = await Promise.all([
        subscriptionService.getCreatorPlan(creatorId),
        subscriptionService.listMine(),
      ]);

      if (cancelled || !mountedRef.current) {
        return;
      }

      if (planResult.success) {
        setPlan(planResult.plan);
      }
      if (mineResult.success) {
        setSubscription(findSubscription(mineResult.subscriptions, creatorId));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [creatorId, isOwnProfile, loadPage]);

  // Infinite scroll. The observer is rebuilt whenever the list grows: observing
  // a sentinel that is already on screen re-fires the callback, which is what
  // keeps paging going when a short page does not push it out of view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          loadMore();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [loadMore, state.hasMore, state.posts.length]);

  const refreshAccess = useCallback(async () => {
    const result = await subscriptionService.listMine();
    if (!mountedRef.current || !result.success) {
      return;
    }
    setSubscription(findSubscription(result.subscriptions, creatorId));
  }, [creatorId]);

  const accessState = useMemo(
    () => subscriptionAccessState(subscription, new Date()),
    [subscription],
  );

  const periodEnd = formatPeriodEnd(subscription?.currentPeriodEnd ?? null);

  // No price to show means no way to subscribe: a creator on their own profile,
  // a viewer who already has access, or a creator with no usable plan.
  const ctaPrice =
    isOwnProfile || accessState !== 'none' ? null : plan?.monthlyPriceCredits ?? null;

  const handleSubscribe = useCallback(async () => {
    // Guarded rather than merely hidden: without a plan the backend would
    // answer 404, so the request is never sent.
    if (busy || isOwnProfile || plan === null) {
      return;
    }

    setBusy(true);
    setSubscribeError(null);

    const result = await subscriptionService.subscribe(creatorId);
    if (!mountedRef.current) {
      return;
    }

    if (result.success) {
      await refreshAccess();
      if (!mountedRef.current) {
        return;
      }
      reloadFromStart();
      toast.success(`Subscribed to ${creatorName}`);
      setBusy(false);
      return;
    }

    const failure = subscribeFailure(result.status, result.message);

    if (failure.alreadySubscribed) {
      // The row exists after all; catch up rather than report a conflict.
      await refreshAccess();
      if (!mountedRef.current) {
        return;
      }
      reloadFromStart();
      setBusy(false);
      return;
    }

    if (failure.showBuyCredits) {
      // Inline, next to a link that fixes it.
      setSubscribeError(failure);
    } else {
      toast.error(failure.message);
    }

    setBusy(false);
  }, [busy, creatorId, creatorName, isOwnProfile, plan, refreshAccess, reloadFromStart]);

  const handleCancel = useCallback(async () => {
    if (busy || isOwnProfile) {
      return;
    }

    setBusy(true);
    setSubscribeError(null);

    const result = await subscriptionService.cancel(creatorId);
    if (!mountedRef.current) {
      return;
    }

    if (result.success) {
      // The timeline is deliberately not reloaded: access runs to the end of
      // the period already paid for, so nothing re-locks today.
      if (result.subscription) {
        setSubscription(result.subscription);
      } else {
        await refreshAccess();
      }
      if (mountedRef.current) {
        toast.success('Subscription cancelled');
      }
    } else {
      toast.error(subscribeFailure(result.status, result.message).message);
    }

    if (mountedRef.current) {
      setBusy(false);
    }
  }, [busy, creatorId, isOwnProfile, refreshAccess]);

  /**
   * Put a cancelled-but-still-paid subscription back. The same endpoint as
   * subscribing: the backend resumes the existing row for free (200) or, if the
   * paid period lapsed in the meantime, charges a fresh month (201).
   *
   * Deliberately not guarded on `plan === null`, unlike `handleSubscribe`: the
   * cancelled panel shows whether or not the creator still sells subscriptions,
   * and a fan holding a row deserves the server's reason over a dead button.
   */
  const handleResume = useCallback(async () => {
    if (busy || isOwnProfile) {
      return;
    }

    setBusy(true);
    setSubscribeError(null);

    const result = await subscriptionService.subscribe(creatorId);
    if (!mountedRef.current) {
      return;
    }

    if (result.success) {
      if (result.subscription) {
        setSubscription(result.subscription);
      } else {
        await refreshAccess();
      }
      if (!mountedRef.current) {
        return;
      }

      if (wasResumedWithoutCharge(result.status)) {
        // Access never lapsed, so nothing on screen re-locked: no reload.
        toast.success('Subscription resumed — no credits taken');
      } else {
        // 201: the period had lapsed and a fresh month was charged, so posts
        // that were locked a moment ago are not any more.
        reloadFromStart();
        toast.success(`Subscribed to ${creatorName}`);
      }

      setBusy(false);
      return;
    }

    const failure = subscribeFailure(result.status, result.message);

    if (failure.alreadySubscribed) {
      // Another tab got there first; catch up rather than report a conflict.
      await refreshAccess();
      if (mountedRef.current) {
        setBusy(false);
      }
      return;
    }

    if (failure.showBuyCredits) {
      setSubscribeError(failure);
    } else {
      toast.error(failure.message);
    }

    setBusy(false);
  }, [busy, creatorId, creatorName, isOwnProfile, refreshAccess, reloadFromStart]);

  const firstPageLoading = state.status === 'loading' && state.posts.length === 0;

  return (
    <div className="w-full max-w-full space-y-4">
      {!isOwnProfile && (
        <SubscribeCard
          creatorName={creatorName}
          priceCredits={accessState === 'none' ? plan?.monthlyPriceCredits ?? null : null}
          state={accessState}
          periodEnd={periodEnd}
          busy={busy}
          error={subscribeError}
          onSubscribe={() => void handleSubscribe()}
          onCancel={() => void handleCancel()}
          onResume={() => void handleResume()}
        />
      )}

      {firstPageLoading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400"></div>
          Loading posts…
        </div>
      ) : timelineIsEmpty(state) ? (
        <div className="w-full max-w-full bg-white p-6 rounded-xl shadow-lg border border-gray-200 text-center">
          <p className="text-sm text-gray-600">No posts yet</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {state.posts.map(post => (
            <TimelinePostCard
              key={post.id}
              post={post}
              priceCredits={ctaPrice}
              subscribing={busy}
              onSubscribe={() => void handleSubscribe()}
              onRefreshPost={() => void handleRefreshPost()}
              refreshing={refreshing}
            />
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <div className="w-full max-w-full bg-white p-4 rounded-xl border border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-red-600 break-words">{state.error}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="w-full sm:w-auto shrink-0 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      )}

      {state.hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-4">
          {state.status === 'loadingMore' && (
            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gray-400"></div>
          )}
        </div>
      )}
    </div>
  );
};

export default CreatorTimeline;
